import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Description as DocumentIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { FileDropzone } from "../common/FileDropzone";
import { FileUpload } from "./FileUpload";
import type { FileUploadItem } from "../../hooks/useFileUpload";
import type { JobResponse } from "@taxassist/api-client";
import { deliverableChipStyles } from "../../styles/chips";
import { ARTIFACT_TYPE_LABELS, type ProcessingArtifactType } from "./fileList";

// Unified upload mode configuration
export type UploadMode = "general" | "processing-results";

// Processing result artifact types - reuse shared types
export type ArtifactType = Exclude<ProcessingArtifactType, "other">;

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

interface FileUploadDialogProps {
  open: boolean;
  onClose: () => void;
  mode: UploadMode;

  // General upload mode props
  job?: JobResponse | null;
  onUploadComplete?: (files: FileUploadItem[]) => void;

  // Processing results mode props
  jobId?: string;
  onProcessingUpload?: (
    file: File,
    artifactType: ArtifactType,
    description?: string,
  ) => Promise<void>;

  // Common props
  onError?: (error: Error) => void;
  title?: string;
  subtitle?: string;
}

export function FileUploadDialog({
  open,
  onClose,
  mode,
  job,
  onUploadComplete,
  jobId,
  onProcessingUpload,
  onError,
  title,
  subtitle,
}: FileUploadDialogProps) {
  // Processing results mode state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [artifactType, setArtifactType] = useState<ArtifactType>("binder");
  const [description, setDescription] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setSelectedFile(null);
      setArtifactType("binder");
      setDescription("");
      setUploading(false);
      setUploadComplete(false);
      setUploadError(null);
    }
  }, [open]);

  // General upload mode handlers
  const handleGeneralUploadComplete = (files: FileUploadItem[]) => {
    onUploadComplete?.(files);
  };

  // Processing results mode handlers
  const handleFilesSelected = useCallback((files: File[]) => {
    if (files.length > 0) {
      setSelectedFile(files[0]);
      setUploadError(null);
    }
  }, []);

  const handleDropzoneError = useCallback(
    (error: Error) => {
      setUploadError(error.message);
      onError?.(error);
    },
    [onError],
  );

  const handleProcessingUploadAction = useCallback(async () => {
    if (!selectedFile || !onProcessingUpload) return;

    setUploading(true);
    setUploadError(null);

    try {
      await onProcessingUpload(selectedFile, artifactType, description);
      setUploadComplete(true);

      // Auto-close after successful upload
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Upload failed";
      setUploadError(errorMessage);
      onError?.(error instanceof Error ? error : new Error(errorMessage));
    } finally {
      setUploading(false);
    }
  }, [
    selectedFile,
    artifactType,
    description,
    onProcessingUpload,
    onError,
    onClose,
  ]);

  const handleCancel = useCallback(() => {
    if (!uploading) {
      // Small delay to ensure proper focus restoration
      setTimeout(() => {
        onClose();
      }, 50);
    }
  }, [uploading, onClose]);

  // Dynamic props based on mode
  const dialogTitle =
    title ||
    (mode === "processing-results"
      ? "Upload Processing Result"
      : "Upload Files");
  const dialogSubtitle =
    subtitle ||
    (mode === "general" && job
      ? `${job.clientName} - ${job.filingType} ${job.taxYear}`
      : undefined);
  const canUpload =
    mode === "processing-results"
      ? selectedFile && !uploading && !uploadComplete
      : true;

  // Validation for required props
  if (mode === "general" && !job) {
    console.error("FileUploadDialog: job prop is required for general mode");
    return null;
  }

  if (mode === "processing-results" && (!jobId || !onProcessingUpload)) {
    console.error(
      "FileUploadDialog: jobId and onProcessingUpload props are required for processing-results mode",
    );
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={
        mode === "processing-results" && uploading ? undefined : handleCancel
      }
      maxWidth="md"
      fullWidth
      disableAutoFocus={false}
      disableEnforceFocus={false}
      disableRestoreFocus={false}
      aria-labelledby="file-upload-dialog-title"
      aria-describedby="file-upload-dialog-content"
      slotProps={{
        paper: {
          sx: {
            minHeight: mode === "processing-results" ? "500px" : "400px",
          },
        },
      }}
    >
      <DialogTitle id="file-upload-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">{dialogTitle}</Typography>
            {dialogSubtitle && (
              <Typography variant="body2" color="text.secondary">
                {dialogSubtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCancel}
            disabled={mode === "processing-results" && uploading}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers id="file-upload-dialog-content">
        {mode === "general" && job ? (
          /* General upload mode - use existing FileUpload component */
          <Box sx={{ pt: 2 }}>
            <FileUpload
              jobId={job.jobId}
              onUploadComplete={handleGeneralUploadComplete}
              onError={onError}
            />
          </Box>
        ) : (
          /* Processing results mode - custom single file upload with metadata */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
            {/* File Selection */}
            {!selectedFile ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Select File to Upload
                </Typography>
                <FileDropzone
                  onFilesSelected={handleFilesSelected}
                  onError={handleDropzoneError}
                  maxFiles={1}
                  maxFileSize={Infinity}
                  disabled={uploading || uploadComplete}
                  loading={uploading}
                  title="Drag & drop processing result here"
                  buttonText="Select Processing Result"
                  acceptedFileTypes={[
                    "application/pdf",
                    "application/msword",
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    "application/vnd.ms-excel",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "text/plain",
                    "image/png",
                    "image/jpeg",
                    "image/jpg",
                  ]}
                  helperText="Maximum 1 file. Accepted formats: PDF, Word, Excel, images"
                />
              </Box>
            ) : (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Selected File:
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    backgroundColor: "grey.50",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DocumentIcon color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedFile.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(selectedFile.size)}
                      </Typography>
                    </Box>
                  </Box>

                  {uploadComplete && (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Uploaded"
                      color="success"
                      size="small"
                    />
                  )}

                  {!uploading && !uploadComplete && (
                    <Button
                      size="small"
                      onClick={() => setSelectedFile(null)}
                      color="inherit"
                    >
                      Change
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {/* Artifact Type Selection */}
            {selectedFile && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Deliverable Type
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {Object.entries(ARTIFACT_TYPE_LABELS).map(
                    ([value, label]) => {
                      const isSelected =
                        artifactType === (value as ArtifactType);
                      return (
                        <Chip
                          key={value}
                          label={label}
                          color="primary"
                          onClick={() =>
                            !uploading &&
                            !uploadComplete &&
                            setArtifactType(value as ArtifactType)
                          }
                          disabled={uploading || uploadComplete}
                          clickable
                          size="small"
                          sx={{
                            ...deliverableChipStyles,
                            "&:hover": {
                              backgroundColor: "action.hover",
                            },
                            ...(isSelected
                              ? {
                                  backgroundColor: "info.light",
                                  border: "2px solid",
                                  borderColor: "primary.main",
                                  color: "primary.main",
                                  "&:hover": {
                                    backgroundColor: "secondary.light",
                                  },
                                  "& .MuiChip-label": {
                                    ...deliverableChipStyles[
                                      "& .MuiChip-label"
                                    ],
                                    color: "primary.main",
                                  },
                                }
                              : {}),
                          }}
                        />
                      );
                    },
                  )}
                </Box>
              </Box>
            )}

            {/* Description */}
            {selectedFile && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description (Optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={uploading || uploadComplete}
                placeholder="Add a description for this processing result..."
                helperText="Provide additional context about this file"
              />
            )}

            {/* Upload Progress/Status */}
            {uploading && (
              <Alert
                severity="info"
                icon={<CircularProgress size={16} />}
                sx={{ "& .MuiAlert-icon": { alignItems: "center" } }}
              >
                Uploading {selectedFile?.name}...
              </Alert>
            )}

            {uploadComplete && (
              <Alert severity="success">
                Successfully uploaded {selectedFile?.name} as{" "}
                {ARTIFACT_TYPE_LABELS[artifactType]}
              </Alert>
            )}

            {uploadError && <Alert severity="error">{uploadError}</Alert>}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleCancel}
          disabled={mode === "processing-results" && uploading}
        >
          {mode === "processing-results" && uploadComplete
            ? "Close"
            : mode === "processing-results"
              ? "Cancel"
              : "Close"}
        </Button>

        {mode === "processing-results" && !uploadComplete && (
          <Button
            onClick={handleProcessingUploadAction}
            variant="contained"
            color="primary"
            disabled={!canUpload}
            startIcon={
              uploading ? <CircularProgress size={16} /> : <UploadIcon />
            }
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
